import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  LayoutDashboard, Users, Briefcase, Plus, Search, ChevronRight, ChevronLeft, X, DollarSign, 
  Clock, CheckCircle2, XCircle, HelpCircle, ArrowRightLeft, History, Presentation, Calculator, 
  Table, Columns, Link as LinkIcon, Trash2, BarChart3, Download, Pencil, Hexagon, ArrowUpDown, 
  LogOut, Mail, FileText, File, ExternalLink, FileSpreadsheet, Files, Zap, Lightbulb, Phone, 
  Utensils, Coffee, MessageSquare, Calendar as CalendarIcon, Shield, TrendingUp, Target, Activity,
  PieChart, Percent, Check
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged, 
  signInWithCustomToken 
} from 'firebase/auth';
import { 
  getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, 
  serverTimestamp, query, orderBy 
} from 'firebase/firestore';

// --- CONFIGURATION & INIT ---
const firebaseConfig = {
  apiKey: "AIzaSyDdLuGW7FYt-ZFYJffz6-BasKhfA_CPejY",
  authDomain: "mercury-carbon-crm.firebaseapp.com",
  projectId: "mercury-carbon-crm",
  storageBucket: "mercury-carbon-crm.firebasestorage.app",
  messagingSenderId: "59510757092",
  appId: "1:59510757092:web:dc3332d9f1281a3eae7ef1"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();
const appId = 'mercury-carbon-live';
const DB_PATH_SEGMENTS = ['artifacts', appId, 'public', 'data'];

const BRAND = {
  primary: "bg-[#0F172A]", primaryHover: "hover:bg-[#1E293B]", accent: "bg-[#10B981]", 
  textPrimary: "text-[#0F172A]", textLight: "text-slate-400"
};

const CURRENCIES = {
  GBP: { symbol: '£', label: 'GBP (£)' }, USD: { symbol: '$', label: 'USD ($)' },
  EUR: { symbol: '€', label: 'EUR (€)' }, AUD: { symbol: 'A$', label: 'AUD (A$)' },
  CAD: { symbol: 'C$', label: 'CAD (C$)' },
};

const EXCHANGE_RATES = { GBP: 1.00, USD: 0.79, EUR: 0.85, AUD: 0.52, CAD: 0.58 };

const calculateCommissions = (amount, commRate, payawayRate, nomadShare) => {
  const i = parseFloat(amount) || 0, c = parseFloat(commRate) || 0;
  const netBeforeNomad = (i * c) - (i * (parseFloat(payawayRate) || 0));
  const nomad = Math.max(0, netBeforeNomad * (parseFloat(nomadShare) || 0));
  return { grossCommission: i * c, payawayAmount: i * (parseFloat(payawayRate) || 0), nomadAmount: nomad, finalNet: Math.max(0, netBeforeNomad - nomad) };
};

const formatCurrency = (val, cur = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
const formatPercent = (val) => `${((parseFloat(val) || 0) * 100).toFixed(1)}%`;

// --- AUDIT LOGGER ---
const logAudit = async (action, details, target = null) => {
  const user = auth.currentUser?.email || 'Unknown';
  try {
    await addDoc(collection(db, ...DB_PATH_SEGMENTS, 'audit_logs'), {
      action, details, user, target, createdAt: serverTimestamp()
    });
  } catch (e) { console.error("Audit Error:", e); }
};

// --- REUSABLE COMPONENTS ---

function SearchableSelect({ options, value, onChange, placeholder, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className="relative" ref={wrapperRef}>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>}
      <div 
        className="w-full p-2 border border-slate-300 rounded-lg bg-white flex justify-between items-center cursor-pointer hover:border-emerald-500 transition-colors"
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) setSearch(''); }}
      >
        <span className={selectedOption ? 'text-slate-800' : 'text-slate-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronUpDown size={16} className="text-slate-400" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100">
          <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md outline-none focus:border-emerald-500"
                placeholder="Type to search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-xs text-slate-400 italic">No matches found</div>
            ) : (
              filteredOptions.map(opt => (
                <div 
                  key={opt.value}
                  className={`px-3 py-2 text-sm rounded-md cursor-pointer flex items-center justify-between ${value === opt.value ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700 hover:bg-slate-100'}`}
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                >
                  {opt.label}
                  {value === opt.value && <Check size={14} />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TagInput({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const tags = value ? value.split(',').map(t => t.trim()).filter(t => t) : [];

  const addTag = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      const newTag = input.trim();
      if (!tags.includes(newTag)) {
        const newTags = [...tags, newTag];
        onChange(newTags.join(', '));
      }
      setInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    onChange(newTags.join(', '));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">Tags</label>
      <div className="flex flex-wrap items-center gap-2 p-2 border border-slate-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
        {tags.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-md border border-slate-200">
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors"><X size={12} /></button>
          </span>
        ))}
        <input 
          type="text" 
          value={input} 
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={addTag}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] outline-none text-sm py-1 bg-transparent"
        />
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5 ml-1">Press Enter to add tag</p>
    </div>
  );
}

function ChevronUpDown({ size, className }) {
  return (
    <div className={`flex flex-col ${className}`}>
      <ArrowUpDown size={size} />
    </div>
  );
}

function StatusBadge({ status, mini }) {
  const S = { 'Invested': 'bg-emerald-100 text-emerald-800 border-emerald-200', 'Committed': 'bg-emerald-100 text-emerald-800 border-emerald-200', 'Interested': 'bg-yellow-100 text-yellow-800 border-yellow-200', 'Pitched': 'bg-indigo-50 text-indigo-700 border-indigo-100', 'Approached': 'bg-blue-50 text-blue-700 border-blue-100', 'Passed': 'bg-slate-100 text-slate-600 border-slate-200' };
  return <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${S[status] || S['Approached']}`}>{['Invested','Committed'].includes(status) ? <CheckCircle2 size={mini?12:14} /> : status === 'Interested' ? <HelpCircle size={mini?12:14} /> : status === 'Passed' ? <XCircle size={mini?12:14} /> : <Clock size={mini?12:14} />}{!mini && status}</span>;
}

function StatCard({ title, value, icon, bg, textColor, subtext }) {
  return (
    <div className={`${bg} p-6 rounded-xl shadow-lg border border-slate-200/50 flex items-center justify-between relative overflow-hidden group`}>
      <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-transform transform scale-150">{React.cloneElement(icon, { size: 80 })}</div>
      <div className="relative z-10"><p className={`text-xs font-bold uppercase tracking-widest mb-1 opacity-70 ${textColor === 'text-white' ? 'text-slate-300' : 'text-slate-500'}`}>{title}</p><h3 className={`text-3xl font-bold ${textColor} tracking-tight`}>{value}</h3>{subtext && <p className={`text-[10px] mt-1 uppercase tracking-wider opacity-60 ${textColor === 'text-white' ? 'text-slate-300' : 'text-slate-500'}`}>{subtext}</p>}</div>
      <div className={`p-3 rounded-lg backdrop-blur-sm relative z-10 shadow-sm ${textColor === 'text-white' ? 'bg-white/10 border border-white/10' : 'bg-slate-50 border border-slate-100'}`}>{icon}</div>
    </div>
  );
}

function SmartMatch({ deal, contacts, onMatchClick }) {
  const matches = useMemo(() => {
    if (!deal.tags || !contacts) return [];
    const dt = deal.tags.toLowerCase().split(',').map(t => t.trim());
    return contacts.filter(c => c.tags && dt.some(tag => c.tags.toLowerCase().includes(tag))).slice(0, 5);
  }, [deal, contacts]);
  if (!matches.length) return null;
  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3"><Lightbulb className="text-indigo-600" size={18} /><h3 className="font-bold text-indigo-900 text-sm">Smart Match Suggestions</h3></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{matches.map(c => (
        <div key={c.id} className="bg-white p-3 rounded-lg border border-indigo-100 shadow-sm flex justify-between items-center group hover:border-indigo-300">
          <div><p className="font-bold text-slate-800 text-sm">{c.name}</p><p className="text-xs text-slate-500 truncate">{c.company || 'Investor'}</p></div>
          <button onClick={() => onMatchClick(c)} className="p-1.5 bg-indigo-100 text-indigo-600 rounded-md hover:bg-indigo-600 hover:text-white"><Plus size={14} /></button>
        </div>
      ))}</div>
    </div>
  );
}

function PipelineColumn({ title, items, contacts, color, currency, onAdd, onDelete, onEdit, onSelectContact }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4 flex flex-col h-full min-h-[300px] border border-slate-200 overflow-hidden">
      <div className={`flex justify-between items-center mb-4 pb-2 border-b border-slate-200`}>
        <div className="flex items-center gap-2"><h3 className="font-bold text-slate-700 text-sm uppercase">{title}</h3><span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-xs font-bold">{items.length}</span></div>
        <button onClick={onAdd} className="p-1 hover:bg-slate-200 text-slate-400 hover:text-blue-600 rounded"><Plus size={16} /></button>
      </div>
      <div className="space-y-3 overflow-y-auto flex-1 pr-1 custom-scrollbar">
        {items.map(item => (
          <div key={item.id} className={`bg-white p-3 rounded-lg shadow-sm border-l-4 ${color} hover:shadow-md transition-shadow group relative`}>
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100"><button onClick={(e) => { e.stopPropagation(); onEdit(item); }} className="text-slate-300 hover:text-blue-500"><Pencil size={14} /></button><button onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button></div>
            <button className="font-bold text-slate-800 text-sm mb-1 hover:text-emerald-600 hover:underline text-left block" onClick={() => onSelectContact(item.contactId)}>{contacts.find(c => c.id === item.contactId)?.name || 'Unknown'}</button>
            {item.amount > 0 && <p className="text-emerald-600 text-xs font-semibold mb-1">{formatCurrency(item.amount, currency)}</p>}
            {item.notes && <p className="text-xs text-slate-500 line-clamp-2 italic">"{item.notes}"</p>}
            <div className="text-[10px] text-slate-400 mt-2 text-right">{item.date}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- MAIN COMPONENTS ---

function LoginScreen() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError(null);
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      if (!user?.email?.endsWith('@mercurycarbon.com')) throw new Error("Restricted Access");
      await logAudit('LOGIN', `User logged in: ${user.email}`);
    } catch (err) {
      await signOut(auth);
      setError(err.message === "Restricted Access" ? "Access Restricted: Use @mercurycarbon.com email." : "Login failed.");
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${BRAND.primary} flex flex-col items-center justify-center p-4 text-white`}>
      <div className="w-full max-w-md bg-[#1E293B] p-8 rounded-2xl shadow-2xl border border-slate-700 animate-in fade-in zoom-in duration-500">
        <div className="flex flex-col items-center mb-10">
          <div className="w-20 h-20 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center text-emerald-500 shadow-inner mb-6 border border-slate-600"><Hexagon size={40} strokeWidth={2} /></div>
          <h1 className="text-3xl font-bold tracking-widest text-white">MERCURY <span className="text-emerald-500">CARBON</span></h1>
          <p className="text-slate-400 text-xs mt-3 tracking-[0.2em] uppercase">Restricted Internal Use Only</p>
        </div>
        <div className="space-y-6">
          <button onClick={handleLogin} disabled={loading} className="w-full bg-white text-slate-800 font-bold py-3.5 rounded-lg flex items-center justify-center gap-3 transition-all hover:bg-slate-100 disabled:opacity-70">
            {loading ? <span className="animate-pulse">Verifying...</span> : <>Sign in with Mercury Carbon</>}
          </button>
          {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded text-center text-sm font-medium">{error}</div>}
        </div>
      </div>
    </div>
  );
}

function Sidebar({ activeView, setActiveView, user }) {
  return (
    <div className={`w-20 lg:w-64 ${BRAND.primary} text-white flex flex-col h-screen flex-shrink-0 border-r border-slate-800 shadow-2xl transition-all duration-300`}>
      <div className="p-6 flex items-center justify-center lg:justify-start gap-3 border-b border-slate-800 h-24">
        <div className="text-emerald-500"><Hexagon size={28} strokeWidth={2.5} /></div>
        <div className="hidden lg:flex flex-col"><span className="font-bold tracking-widest text-lg leading-none">MERCURY</span><span className="font-medium tracking-[0.2em] text-xs text-slate-400 leading-none mt-1">CARBON</span></div>
      </div>
      <nav className="flex-1 py-6 space-y-2 px-3 overflow-y-auto">
        {[
          { id: 'dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
          { id: 'deals', icon: <Briefcase size={20} />, label: 'Deals', activeIds: ['deals', 'deal-detail'] },
          { id: 'contacts', icon: <Users size={20} />, label: 'Clients', activeIds: ['contacts', 'contact-detail'] },
          { id: 'calendar', icon: <CalendarIcon size={20} />, label: 'Calendar' },
          { id: 'history', icon: <History size={20} />, label: 'History' },
          { id: 'audit', icon: <Shield size={20} />, label: 'Compliance' },
        ].map(item => (
          <button key={item.id} onClick={() => setActiveView(item.id)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-md transition-all ${(item.activeIds || [item.id]).includes(activeView) ? 'bg-gradient-to-r from-[#1E293B] to-[#334155] text-white shadow-lg border-l-4 border-emerald-500' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}>
            <div className="flex-shrink-0">{item.icon}</div><span className="hidden lg:block font-medium text-sm">{item.label}</span>
          </button>
        ))}
        <div className="pt-4 mt-4 border-t border-slate-800">
          <p className="px-3 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 hidden lg:block">Tools</p>
          <button onClick={() => setActiveView('calculator')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-md transition-all ${activeView === 'calculator' ? 'bg-gradient-to-r from-[#1E293B] to-[#334155] text-white shadow-lg border-l-4 border-emerald-500' : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}>
            <Calculator size={20} /><span className="hidden lg:block font-medium text-sm">Revenue Calc</span>
          </button>
        </div>
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="mb-4 px-2 hidden lg:block"><p className="text-xs text-slate-400">Logged in as:</p><p className="text-xs font-bold truncate text-emerald-500">{user?.email}</p></div>
        <button onClick={() => signOut(auth)} className="text-xs text-slate-500 hover:text-white flex items-center gap-2 w-full justify-center lg:justify-start px-2 py-2 rounded hover:bg-white/5"><LogOut size={14} /><span className="hidden lg:inline font-medium">Sign Out</span></button>
      </div>
    </div>
  );
}

function AuditLogView({ logs, contacts, deals, onNavigate }) {
  const resolveTarget = (t) => {
    if (!t) return null;
    if (t.type === 'contact') return contacts.find(c => c.id === t.id);
    if (t.type === 'deal') return deals.find(d => d.id === t.id);
    return null;
  };

  return (
    <div className="h-full flex flex-col space-y-4 pb-6 animate-in fade-in">
      <div className="flex justify-between items-center"><h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>Compliance Log</h1><p className="text-sm text-slate-500">Audit trail of all system activity.</p></div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 overflow-hidden flex flex-col">
        <div className="overflow-auto flex-1 custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase sticky top-0">
              <tr><th className="px-4 py-3">Time</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Details</th><th className="px-4 py-3">Related To</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-slate-400">No logs found.</td></tr> : logs.map(l => {
                const targetExists = resolveTarget(l.target);
                return (
                  <tr key={l.id} className="hover:bg-slate-50 group">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap font-mono text-xs">{l.createdAt?.seconds ? new Date(l.createdAt.seconds * 1000).toLocaleString() : '-'}</td>
                    <td className="px-4 py-3 font-medium text-slate-700">{l.user}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${l.action === 'DELETE' ? 'bg-red-50 text-red-700 border-red-100' : l.action === 'LOGIN' ? 'bg-blue-50 text-blue-700 border-blue-100' : l.action === 'UPDATE' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>{l.action}</span></td>
                    <td className="px-4 py-3 text-slate-600 break-words max-w-sm">{l.details}</td>
                    <td className="px-4 py-3">
                      {l.target ? (
                        targetExists ? (
                          <button 
                            onClick={() => onNavigate(l.target.type, l.target.id)} 
                            className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-800 hover:underline font-bold transition-colors"
                          >
                             {l.target.type === 'contact' ? <Users size={12} /> : <Briefcase size={12} />}
                             {l.target.name}
                          </button>
                        ) : (
                          <span className="text-slate-400 italic flex items-center gap-1.5">
                             <Trash2 size={12} />
                             {l.target.name} (Deleted)
                          </span>
                        )
                      ) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CalendarView({ interactions, contacts, deals, onEventClick, onDeleteEvent, setSelectedContact, setSelectedDeal, setActiveView }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const eventsByDate = useMemo(() => {
    const map = {};
    interactions.forEach(i => {
      if (!i.date) return;
      if (!map[i.date]) map[i.date] = [];
      const contact = contacts.find(c => c.id === i.contactId), deal = deals.find(d => d.id === i.dealId);
      map[i.date].push({ ...i, contactName: contact?.name || 'Unknown', dealName: deal?.name, style: getEventStyle(i.status) });
    });
    return map;
  }, [interactions, contacts, deals]);

  function getEventStyle(status) {
    const styles = {
      'Invested': 'bg-emerald-100 text-emerald-800 border-emerald-200', 'Committed': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Interested': 'bg-amber-100 text-amber-800 border-amber-200', 'Pitched': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Approached': 'bg-blue-100 text-blue-800 border-blue-200', 'Meeting': 'bg-purple-100 text-purple-800 border-purple-200',
      'Call': 'bg-cyan-100 text-cyan-800 border-cyan-200', 'Lunch': 'bg-orange-100 text-orange-800 border-orange-200',
      'Email': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return { className: styles[status] || 'bg-slate-100 text-slate-800 border-slate-200' };
  }

  const getStartOfWeek = (d) => { const date = new Date(d); date.setDate(date.getDate() - date.getDay()); return date; };
  const startOfWeek = getStartOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek); d.setDate(d.getDate() + i);
    return { date: d, key: d.toISOString().split('T')[0], isToday: new Date().toDateString() === d.toDateString() };
  });

  return (
    <div className="flex flex-col h-full space-y-4 pb-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center flex-shrink-0">
        <div><h1 className="text-2xl font-bold text-slate-900">Weekly Schedule</h1><p className="text-slate-500 text-sm">Activity management.</p></div>
        <div className="flex items-center gap-4">
            <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
            <button onClick={() => { const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d); }} className="p-2 hover:bg-slate-100 rounded-md text-slate-600"><ChevronLeft size={20} /></button>
            <span className="px-4 font-bold text-slate-800 min-w-[150px] text-center">{startOfWeek.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} - {new Date(startOfWeek.getTime() + 6 * 86400000).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <button onClick={() => { const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d); }} className="p-2 hover:bg-slate-100 rounded-md text-slate-600"><ChevronRight size={20} /></button>
            </div>
            <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200">Today</button>
        </div>
      </div>
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
        <div className="flex flex-1 overflow-x-auto divide-x divide-slate-200">
          {days.map(({ date, key, isToday }, i) => (
            <div key={i} className={`flex-1 flex flex-col min-w-[150px] border-r border-slate-200 last:border-r-0 ${isToday ? 'bg-emerald-50/30' : 'bg-white'}`}>
              <div className={`p-3 text-center border-b border-slate-200 ${isToday ? 'bg-emerald-50' : ''}`}>
                 <p className={`text-xs font-bold uppercase ${isToday ? 'text-emerald-600' : 'text-slate-400'}`}>{date.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                 <div className={`text-xl font-bold ${isToday ? 'text-emerald-700' : 'text-slate-700'}`}>{date.getDate()}</div>
              </div>
              <div className="flex-1 p-2 space-y-2 overflow-y-auto custom-scrollbar">
                {(eventsByDate[key] || []).map((evt) => (
                  <div key={evt.id} onClick={() => onEventClick(evt)} className={`relative group p-3 rounded-lg border-l-4 shadow-sm hover:shadow-md transition-all cursor-pointer ${evt.style.className}`}>
                    <div className="pr-5">
                       <div className="font-bold text-sm truncate"><span onClick={(e) => { e.stopPropagation(); const c = contacts.find(c => c.id === evt.contactId); if(c) { setSelectedContact(c); setActiveView('contact-detail'); }}} className="hover:underline">{evt.contactName}</span></div>
                       <div className="text-xs opacity-90 mt-0.5 truncate">{evt.dealName ? <span onClick={(e) => { e.stopPropagation(); const d = deals.find(d => d.id === evt.dealId); if(d) { setSelectedDeal(d); setActiveView('deal-detail'); }}} className="hover:underline hover:font-bold">{evt.dealName}</span> : evt.status}</div>
                       {evt.notes && <div className="text-[10px] opacity-70 italic mt-1 line-clamp-2 border-t border-black/5 pt-1">"{evt.notes}"</div>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete event?')) onDeleteEvent(evt.id); }} className="absolute top-2 right-2 p-1 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RevenueCalculator() {
  const [vals, setVals] = useState({ raise: 1000000, comm: 0.06, payaway: 0.02, nomad: 0.20, curr: 'GBP' });
  const stats = useMemo(() => calculateCommissions(vals.raise, vals.comm, vals.payaway, vals.nomad), [vals]);
  const update = (k, v) => setVals(prev => ({ ...prev, [k]: v }));

  return (
    <div className="h-full flex flex-col space-y-6 pb-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center"><div><h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>Revenue Simulator</h1><p className="text-slate-500 text-sm">Model fees and splits.</p></div></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 space-y-6">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-2"><Calculator size={18} className="text-emerald-600" /> Parameters</h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Total Capital Raised</label>
            <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">{CURRENCIES[vals.curr]?.symbol}</span><input type="number" value={vals.raise} onChange={(e) => update('raise', e.target.value)} className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-lg font-mono font-bold text-slate-700" /></div>
            <div className="flex gap-2 mt-2">{['GBP', 'USD', 'EUR'].map(c => <button key={c} onClick={() => update('curr', c)} className={`px-3 py-1 text-xs font-bold rounded border ${vals.curr === c ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`}>{c}</button>)}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { l: 'Comm %', k: 'comm', v: vals.comm }, { l: 'Payaway %', k: 'payaway', v: vals.payaway }, { l: 'Nomad %', k: 'nomad', v: vals.nomad }
            ].map(f => (
              <div key={f.k}><label className="block text-sm font-medium text-slate-700 mb-2">{f.l}</label><input type="number" step="0.01" value={f.v} onChange={(e) => update(f.k, e.target.value)} className="w-full p-2 border border-slate-300 rounded-lg outline-none" /><p className="text-xs text-slate-500 mt-1">{(f.v * 100).toFixed(0)}%</p></div>
            ))}
          </div>
        </div>
        <div className={`${BRAND.primary} p-8 rounded-xl shadow-lg text-white flex flex-col justify-center space-y-8 relative overflow-hidden`}>
           <div className="absolute top-0 right-0 p-8 opacity-5"><DollarSign size={200} /></div>
           <div className="relative z-10"><p className="text-slate-400 font-bold uppercase tracking-widest text-xs mb-1">Gross Commission</p><h2 className="text-4xl font-bold text-white tracking-tight">{formatCurrency(stats.grossCommission, vals.curr)}</h2></div>
           <div className="grid grid-cols-2 gap-4 relative z-10 border-t border-white/10 pt-6">
              {[
                { l: 'Total Payaways', v: stats.payawayAmount }, { l: 'Nomad Split', v: stats.nomadAmount }
              ].map(i => <div key={i.l}><p className="text-red-300/80 font-bold uppercase tracking-widest text-[10px] mb-1">{i.l}</p><h3 className="text-xl font-mono text-red-300">-{formatCurrency(i.v, vals.curr)}</h3></div>)}
           </div>
           <div className="relative z-10 bg-white/10 p-6 rounded-xl border border-white/10 backdrop-blur-sm"><p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">Projected Net Revenue</p><h2 className="text-5xl font-bold text-white tracking-tight">{formatCurrency(stats.finalNet, vals.curr)}</h2></div>
        </div>
      </div>
    </div>
  );
}

function HistoricalStats({ deals, interactions, setActiveView, setSelectedDeal }) {
  const closed = deals.filter(d => d.status === 'Closed');
  const getRate = (did, i) => parseFloat(i?.exchangeRate) || EXCHANGE_RATES[deals.find(d => d.id === did)?.currency || 'USD'] || 1;
  const metrics = interactions.filter(i => i.status === 'Invested' && closed.some(d => d.id === i.dealId)).reduce((acc, i) => ({
    rev: acc.rev + (calculateCommissions(i.amount, i.commRate, i.payawayRate, i.nomadShare).finalNet * getRate(i.dealId, i)),
    raised: acc.raised + ((parseFloat(i.amount) || 0) * getRate(i.dealId, i))
  }), { rev: 0, raised: 0 });

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex justify-between items-center"><h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>Historical Performance</h1><div className="text-sm text-slate-500 font-medium bg-white px-4 py-1.5 rounded-full border border-slate-200 shadow-sm flex items-center gap-2"><History size={14} className="text-emerald-600" /> Lifetime Stats</div></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard title="Lifetime Funds Sourced" value={formatCurrency(metrics.raised, 'GBP')} icon={<DollarSign className="text-emerald-400" />} bg={BRAND.primary} textColor="text-white" subtext="Closed Deals (GBP)" />
        <StatCard title="Lifetime Revenue (MCL)" value={formatCurrency(metrics.rev, 'GBP')} icon={<Calculator className="text-purple-400" />} bg={BRAND.primary} textColor="text-white" subtext="Net Commission (GBP)" />
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-100 bg-slate-50/50"><h2 className="font-bold text-lg text-slate-800 flex items-center gap-2"><CheckCircle2 size={20} className="text-slate-500" /> Closed Deal Archive</h2></div>
        <div className="overflow-x-auto"><table className="w-full text-sm text-left"><thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-100"><tr><th className="px-6 py-3">Deal Name</th><th className="px-6 py-3 text-right">Target</th><th className="px-6 py-3 text-right">Final Raised</th><th className="px-6 py-3 text-right">Closed Date</th></tr></thead><tbody className="divide-y divide-slate-5">
          {closed.length === 0 ? <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-400 italic">No closed deals.</td></tr> : closed.map(d => {
             const raised = interactions.filter(i => i.dealId === d.id && i.status === 'Invested').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
             return <tr key={d.id} onClick={() => { setSelectedDeal(d); setActiveView('deal-detail'); }} className="hover:bg-slate-50 cursor-pointer group"><td className="px-6 py-4 font-bold text-slate-800 group-hover:text-emerald-700">{d.name}</td><td className="px-6 py-4 text-right text-slate-500">{d.targetAmount ? formatCurrency(d.targetAmount, d.currency) : '-'}</td><td className="px-6 py-4 text-right font-medium text-emerald-700">{formatCurrency(raised, d.currency)}</td><td className="px-6 py-4 text-right text-slate-500">{d.createdAt?.seconds ? new Date(d.createdAt.seconds * 1000).toLocaleDateString() : '-'}</td></tr>;
          })}
        </tbody></table></div>
      </div>
    </div>
  );
}

function Dashboard({ contacts, deals, interactions, setActiveView, setSelectedDeal }) {
  const activeDeals = deals.filter(d => d.status === 'Active');
  const activeIds = activeDeals.map(d => d.id);
  const getRate = (did) => EXCHANGE_RATES[deals.find(d => d.id === did)?.currency || 'USD'] || 1;

  // Pipeline Logic
  const pipeline = useMemo(() => {
    const stats = { Approached: 0, Pitched: 0, Interested: 0, Invested: 0 };
    interactions.forEach(i => {
      if (activeIds.includes(i.dealId) && stats[i.status] !== undefined) stats[i.status]++;
    });
    return stats;
  }, [interactions, activeIds]);

  // Financials
  const financials = useMemo(() => {
    let raised = 0, revenue = 0, dealCount = activeDeals.length;
    interactions.forEach(i => {
      if (i.status === 'Invested' && activeIds.includes(i.dealId)) {
        const rate = getRate(i.dealId);
        const amt = parseFloat(i.amount) || 0;
        raised += amt * rate;
        const comms = calculateCommissions(amt, i.commRate, i.payawayRate, i.nomadShare);
        revenue += comms.finalNet * rate;
      }
    });
    return { raised, revenue, dealCount };
  }, [interactions, activeDeals]);

  // Clients Stats
  const clientStats = useMemo(() => {
    const activeClients = new Set(interactions.filter(i => {
        const d = new Date(i.date);
        const now = new Date();
        const diffTime = Math.abs(now - d);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return diffDays <= 30; // Active in last 30 days
    }).map(i => i.contactId)).size;
    return { total: contacts.length, active: activeClients };
  }, [contacts, interactions]);

  // Win Rate (Weighted by Capital Raised vs Total Targets of Active Deals)
  const winRate = useMemo(() => {
    // Total Target of Active Deals (converted to GBP)
    const totalTarget = activeDeals.reduce((sum, d) => {
      const rate = EXCHANGE_RATES[d.currency || 'USD'] || 1;
      return sum + ((parseFloat(d.targetAmount) || 0) * rate);
    }, 0);
    
    // Total Raised in Active Deals (converted to GBP)
    const totalRaised = interactions
      .filter(i => i.status === 'Invested' && activeIds.includes(i.dealId))
      .reduce((sum, i) => {
         const rate = getRate(i.dealId); 
         return sum + ((parseFloat(i.amount) || 0) * rate);
      }, 0);

    return totalTarget > 0 ? (totalRaised / totalTarget) * 100 : 0;
  }, [interactions, activeDeals, activeIds]);

  // Recent Activity Feed
  const recentActivity = useMemo(() => {
    return [...interactions]
      .sort((a, b) => new Date(b.createdAt?.seconds * 1000 || 0) - new Date(a.createdAt?.seconds * 1000 || 0))
      .slice(0, 5);
  }, [interactions]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex justify-between items-center">
        <h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>Executive Dashboard</h1>
        <div className="text-sm text-slate-500 font-medium bg-white px-4 py-1.5 rounded-full border border-slate-200 shadow-sm flex items-center gap-2">
          <Clock size={14} className="text-emerald-600" /> {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* TOP METRICS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Pipeline Revenue" value={formatCurrency(financials.revenue, 'GBP')} icon={<DollarSign className="text-emerald-400" />} bg={BRAND.primary} textColor="text-white" subtext="Net Commission (Active)" />
        <StatCard title="Capital Raised" value={formatCurrency(financials.raised, 'GBP')} icon={<TrendingUp className="text-blue-400" />} bg="bg-white border border-slate-200" textColor="text-slate-800" subtext="Across Active Deals" />
        <StatCard title="Active Deals" value={financials.dealCount} icon={<Briefcase className="text-purple-400" />} bg="bg-white border border-slate-200" textColor="text-slate-800" subtext="Open Placements" />
        <StatCard title="Active Clients" value={`${clientStats.active} / ${clientStats.total}`} icon={<Users className="text-orange-400" />} bg="bg-white border border-slate-200" textColor="text-slate-800" subtext="30-Day Activity" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MAIN CHART AREA - FUNNEL & STATS */}
        <div className="lg:col-span-2 space-y-6">
           {/* FUNNEL */}
           <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h2 className="font-bold text-lg text-slate-800 flex items-center gap-2 mb-6">
                <Target size={20} className="text-emerald-600" /> Pipeline Velocity
              </h2>
              <div className="space-y-4">
                 {[
                   { label: 'Approached', val: pipeline.Approached, color: 'bg-blue-100 text-blue-800 border-blue-200', w: '100%' },
                   { label: 'Pitched', val: pipeline.Pitched, color: 'bg-indigo-100 text-indigo-800 border-indigo-200', w: '80%' },
                   { label: 'Interested', val: pipeline.Interested, color: 'bg-amber-100 text-amber-800 border-amber-200', w: '60%' },
                   { label: 'Invested', val: pipeline.Invested, color: 'bg-emerald-100 text-emerald-800 border-emerald-200', w: '40%' }
                 ].map(stage => (
                   <div key={stage.label} className="relative">
                      <div className={`h-12 rounded-lg flex items-center justify-between px-4 border ${stage.color}`} style={{ width: stage.w }}>
                         <span className="font-bold text-sm">{stage.label}</span>
                         <span className="font-bold text-lg">{stage.val}</span>
                      </div>
                   </div>
                 ))}
              </div>
           </div>

           {/* ACTIVE DEALS TABLE */}
           <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <h2 className="font-bold text-lg text-slate-800 flex items-center gap-2"><BarChart3 size={20} className="text-slate-500" /> Deal Performance</h2>
                <button onClick={() => setActiveView('deals')} className="text-sm text-emerald-600 font-bold hover:underline">View All</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-100">
                    <tr><th className="px-6 py-3">Deal Name</th><th className="px-6 py-3 text-right">Target</th><th className="px-6 py-3 text-right">Raised</th><th className="px-6 py-3 text-right">Progress</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {activeDeals.slice(0, 5).map(deal => {
                       const raised = interactions.filter(i => i.dealId === deal.id && i.status === 'Invested').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
                       const prog = deal.targetAmount ? Math.min((raised / deal.targetAmount) * 100, 100) : 0;
                       return (
                         <tr key={deal.id} onClick={() => { setSelectedDeal(deal); setActiveView('deal-detail'); }} className="hover:bg-slate-50 cursor-pointer group">
                           <td className="px-6 py-4 font-bold text-slate-800 group-hover:text-emerald-700">{deal.name}</td>
                           <td className="px-6 py-4 text-right text-slate-500">{deal.targetAmount ? formatCurrency(deal.targetAmount, deal.currency) : '-'}</td>
                           <td className="px-6 py-4 text-right font-medium text-slate-800">{formatCurrency(raised, deal.currency)}</td>
                           <td className="px-6 py-4 text-right">
                             <div className="flex items-center justify-end gap-2"><span className="text-xs font-bold text-slate-600">{Math.round(prog)}%</span><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${prog}%` }}></div></div></div>
                           </td>
                         </tr>
                       );
                    })}
                  </tbody>
                </table>
              </div>
           </div>
        </div>

        {/* SIDEBAR METRICS */}
        <div className="space-y-6">
           {/* SECONDARY STATS */}
           <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex items-center justify-between">
                 <div>
                   <p className="text-slate-400 text-xs font-bold uppercase">Target Funds Raised Percentage</p>
                   <p className="text-2xl font-bold text-slate-800">{winRate.toFixed(1)}%</p>
                 </div>
                 <div className="bg-purple-50 p-3 rounded-full text-purple-600"><Percent size={24} /></div>
              </div>
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 flex items-center justify-between">
                 <div>
                   <p className="text-slate-400 text-xs font-bold uppercase">Avg Ticket</p>
                   <p className="text-2xl font-bold text-slate-800">{financials.dealCount > 0 ? formatCurrency(financials.raised / financials.dealCount, 'GBP') : '-'}</p>
                 </div>
                 <div className="bg-blue-50 p-3 rounded-full text-blue-600"><PieChart size={24} /></div>
              </div>
           </div>

           {/* RECENT ACTIVITY */}
           <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 h-fit">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Activity size={18} className="text-slate-500" /> Live Feed</h3>
              <div className="space-y-4">
                {recentActivity.map(i => {
                   const contact = contacts.find(c => c.id === i.contactId);
                   const deal = deals.find(d => d.id === i.dealId);
                   return (
                     <div key={i.id} className="flex gap-3 items-start border-l-2 border-slate-100 pl-3 pb-2">
                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${i.status === 'Invested' ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                        <div>
                           <p className="text-xs text-slate-500 mb-0.5">{new Date(i.createdAt?.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                           <p className="text-sm font-medium text-slate-800">
                             <span className="font-bold">{contact?.name || 'Unknown'}</span> - {i.status}
                           </p>
                           {deal && <p className="text-xs text-emerald-600 font-medium">{deal.name}</p>}
                        </div>
                     </div>
                   );
                })}
              </div>
           </div>

           {/* QUICK ACTIONS */}
           <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
             <h3 className="font-bold text-slate-700 mb-3 text-sm uppercase">Shortcuts</h3>
             <div className="space-y-2">
               <button onClick={() => setActiveView('contacts')} className="w-full bg-white hover:bg-slate-50 border border-slate-200 p-3 rounded-lg text-left text-sm font-bold text-slate-700 flex items-center gap-2 transition-colors"><Users size={16} /> Add Client</button>
               <button onClick={() => setActiveView('deals')} className="w-full bg-emerald-600 hover:bg-emerald-700 border border-transparent p-3 rounded-lg text-left text-sm font-bold text-white flex items-center gap-2 transition-colors"><Briefcase size={16} /> New Deal</button>
             </div>
           </div>
        </div>
      </div>
    </div>
  );
}

function ContactsList({ contacts, deals, interactions, setActiveView, setSelectedContact, onAddContact, onEditContact, onDeleteContact, onQuickAddToDeal }) {
  const [filters, setFilters] = useState({ search: '', tag: '', invested: 'all', sort: 'name' });
  
  const filtered = useMemo(() => {
    const s = filters.search.toLowerCase();
    return contacts.map(c => {
       const h = interactions.filter(i => i.contactId === c.id), inv = h.filter(i => i.status === 'Invested');
       const total = inv.reduce((sum, i) => sum + ((parseFloat(i.amount) || 0) * (parseFloat(i.exchangeRate) || 1)), 0);
       return { ...c, dealCount: h.length, investedCount: inv.length, totalInvested: total, avgTicket: inv.length ? total / inv.length : 0, hasInvested: inv.length > 0 };
    }).filter(c => 
       (c.name.toLowerCase().includes(s) || c.company?.toLowerCase().includes(s)) &&
       (!filters.tag || (c.tags || '').includes(filters.tag)) &&
       (filters.invested === 'all' || (filters.invested === 'yes' ? c.hasInvested : !c.hasInvested))
    ).sort((a, b) => {
       if (filters.sort === 'ticket_high') return b.avgTicket - a.avgTicket;
       if (filters.sort === 'ticket_low') return a.avgTicket - b.avgTicket;
       if (filters.sort === 'active_high') return b.dealCount - a.dealCount;
       return a.name.localeCompare(b.name); 
    });
  }, [contacts, interactions, deals, filters]);

  const allTags = useMemo(() => Array.from(new Set(contacts.flatMap(c => c.tags ? c.tags.split(',').map(t => t.trim()) : []))).sort(), [contacts]);

  return (
    <div className="h-full flex flex-col pb-6">
      <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-6">
        <div><h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>Investor Directory</h1><p className="text-slate-500 text-sm">Manage relationships.</p></div>
        <button onClick={onAddContact} className={`${BRAND.primary} ${BRAND.primaryHover} text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg font-bold`}><Plus size={18} /> Add Client</button>
      </div>
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col lg:flex-row gap-4 items-center">
         <div className="relative flex-1 w-full"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} /><input type="text" placeholder="Search..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500/20" value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} /></div>
         <div className="flex gap-2 w-full lg:w-auto">
            <select className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" value={filters.tag} onChange={e => setFilters({...filters, tag: e.target.value})}><option value="">All Tags</option>{allTags.map(t => <option key={t} value={t}>{t}</option>)}</select>
            <select className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" value={filters.invested} onChange={e => setFilters({...filters, invested: e.target.value})}><option value="all">Inv: All</option><option value="yes">Invested</option><option value="no">Never</option></select>
            <select className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" value={filters.sort} onChange={e => setFilters({...filters, sort: e.target.value})}><option value="name">Name (A-Z)</option><option value="ticket_high">Ticket (High)</option><option value="active_high">Most Active</option></select>
         </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 pr-2 custom-scrollbar">
          {filtered.length === 0 ? <div className="text-center py-20 text-slate-400"><Users size={48} className="mx-auto mb-4 opacity-20" /><p>No clients found.</p></div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{filtered.map(c => (
                <div key={c.id} onClick={() => { setSelectedContact(c); setActiveView('contact-detail'); }} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-400 hover:shadow-lg cursor-pointer transition-all flex flex-col group relative">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                       <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${c.hasInvested ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{c.name.charAt(0)}</div>
                       <div><h3 className="font-bold text-slate-800 line-clamp-1">{c.name}</h3><p className="text-xs text-slate-500">{c.company || 'Individual'}</p></div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); onQuickAddToDeal(c); }} className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-xs flex items-center gap-1 font-medium"><Zap size={14} /> Link</button>
                  </div>
                  {c.tags && <div className="flex flex-wrap gap-1 mb-4">{c.tags.split(',').slice(0, 3).map((t, i) => <span key={i} className="px-2 py-0.5 bg-slate-50 text-slate-600 text-[10px] rounded border border-slate-100">{t.trim()}</span>)}</div>}
                  <div className="mt-auto grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
                    <div><p className="text-[10px] text-slate-400 uppercase font-bold">Avg Ticket</p><p className="font-bold text-slate-700 text-sm">{c.avgTicket > 0 ? formatCurrency(c.avgTicket, 'GBP') : '-'}</p></div>
                    <div className="text-center"><p className="text-[10px] text-slate-400 uppercase font-bold">Deals</p><span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full text-xs font-bold">{c.dealCount}</span></div>
                    <div className="text-right"><p className="text-[10px] text-slate-400 uppercase font-bold">Total Inv.</p><p className="font-bold text-emerald-700 text-sm">{c.totalInvested > 0 ? ((c.totalInvested / 1000).toFixed(0) + 'k') : '-'}</p></div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 bg-white/90 p-1 rounded shadow-sm"><button onClick={(e) => { e.stopPropagation(); onEditContact(c); }} className="p-1 hover:text-emerald-600"><Pencil size={14} /></button><button onClick={(e) => { e.stopPropagation(); onDeleteContact(c.id); }} className="p-1 hover:text-red-600"><Trash2 size={14} /></button></div>
                </div>
              ))}</div>
          )}
      </div>
    </div>
  );
}

function ContactDetail({ contact, deals, interactions, onBack, onAddInteraction, onDeleteInteraction, onEditContact, setSelectedDeal, setActiveView }) {
  const history = useMemo(() => interactions.filter(i => i.contactId === contact.id).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0) || (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)), [interactions, contact]);
  const invested = history.filter(i => i.status === 'Invested');
  const totalInv = invested.reduce((s, i) => s + ((parseFloat(i.amount) || 0) * (parseFloat(i.exchangeRate) || 1)), 0);
  const avgTicket = invested.length ? totalInv / invested.length : 0;
  
  const dealSummary = useMemo(() => {
    const map = new Map();
    [...history].reverse().forEach(i => {
      if(!i.dealId) return;
      const d = deals.find(x => x.id === i.dealId);
      if(!d) return;
      const ex = map.get(i.dealId) || { dealId: i.dealId, name: d.name, curr: d.currency, status: i.status, inv: 0, comm: 0 };
      ex.status = i.status;
      if (i.status === 'Invested' && i.amount) {
        ex.inv += parseFloat(i.amount);
        ex.comm += calculateCommissions(i.amount, i.commRate, i.payawayRate, i.nomadShare).finalNet;
      }
      map.set(i.dealId, ex);
    });
    return Array.from(map.values()).reverse();
  }, [history, deals]);

  return (
    <div className="h-full flex flex-col space-y-6 pb-6 animate-in slide-in-from-right-8 duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4"><button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full text-slate-500"><ArrowRightLeft size={20} /></button><div><h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>{contact.name}</h1><p className="text-slate-500 text-sm">{contact.company || 'Private'} • {contact.email ? <a href={`mailto:${contact.email}`} className="text-emerald-600 hover:underline font-bold">{contact.email}</a> : 'No email'}</p></div></div>
        <div className="flex gap-2"><button onClick={() => onAddInteraction(contact.id)} className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium hover:bg-slate-50"><MessageSquare size={16} /> Activity</button><button onClick={() => onEditContact(contact)} className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium hover:bg-slate-50"><Pencil size={16} /> Edit</button></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-hidden min-h-0">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 overflow-y-auto">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Users size={18} className="text-slate-600" /> Profile</h3>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6 space-y-4">
            <div><p className="text-xs font-bold text-slate-400 uppercase">Total Invested (MC)</p><div className="text-2xl font-bold text-emerald-700">{formatCurrency(totalInv, 'GBP')}</div></div>
            <div className="pt-3 border-t border-slate-200"><p className="text-xs font-bold text-slate-400 uppercase">Avg. Ticket Size</p><div className="text-lg font-bold text-slate-700">{formatCurrency(avgTicket, 'GBP')}</div></div>
          </div>
          <div className="space-y-4 text-sm">
             <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Email</label><div className="text-slate-700 font-medium break-words">{contact.email ? <a href={`mailto:${contact.email}`} className="text-emerald-600 hover:underline">{contact.email}</a> : '-'}</div></div>
             {[{ l: 'Phone', v: contact.phone }, { l: 'Notes', v: contact.notes }].map(f => (
               <div key={f.l}><label className="block text-xs font-semibold text-slate-400 uppercase mb-1">{f.l}</label><div className="text-slate-700 font-medium break-words">{f.v || '-'}</div></div>
             ))}
             <div><label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Tags</label>
             <div className="flex flex-wrap gap-1">
               {contact.tags ? contact.tags.split(',').map((t,i) => <span key={i} className="px-2 py-1 bg-slate-100 text-slate-600 text-[10px] rounded border border-slate-200 font-semibold">{t.trim()}</span>) : '-'}
             </div></div>
          </div>
        </div>
        <div className="lg:col-span-2 flex flex-col gap-6 overflow-hidden h-full">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-1/2 overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center"><h3 className="font-bold text-slate-800 flex items-center gap-2"><Briefcase size={18} /> Portfolio</h3><button onClick={() => onAddInteraction(contact.id)} className={`${BRAND.primary} ${BRAND.primaryHover} text-white text-xs px-3 py-2 rounded-lg flex items-center gap-2 font-bold`}><LinkIcon size={14} /> Link Deal</button></div>
                <div className="overflow-y-auto p-4 flex-1">
                   <table className="w-full text-sm text-left"><thead className="text-xs text-slate-500 uppercase bg-slate-50 sticky top-0"><tr><th className="px-3 py-2">Deal</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Invested</th><th className="px-3 py-2 text-right">Revenue</th></tr></thead><tbody className="divide-y divide-slate-100">{dealSummary.map(ud => (
                      <tr key={ud.dealId} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setSelectedDeal(deals.find(d => d.id === ud.dealId)); setActiveView('deal-detail'); }}><td className="px-3 py-3 font-medium">{ud.name}</td><td className="px-3 py-3"><StatusBadge status={ud.status} mini /></td><td className="px-3 py-3 text-slate-600">{ud.inv > 0 ? formatCurrency(ud.inv, ud.curr) : '-'}</td><td className="px-3 py-3 text-right font-bold text-emerald-700">{ud.comm > 0 ? formatCurrency(ud.comm, ud.curr) : '-'}</td></tr>
                   ))}</tbody></table>
                </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-1/2 overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50"><h3 className="font-bold text-slate-800 flex items-center gap-2"><History size={18} /> Activity Log</h3></div>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {history.map(i => {
                        const d = deals.find(x => x.id === i.dealId);
                        const Icon = { 'Call': Phone, 'Lunch': Utensils, 'Meeting': Coffee, 'Email': Mail }[i.status] || MessageSquare;
                        return (
                        <div key={i.id} className="relative pl-8 pb-8 last:pb-0 border-l-2 border-slate-100">
                            <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 ${d ? 'bg-white border-emerald-400' : 'bg-slate-100 border-slate-300'}`}></div>
                            <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow -mt-2 group">
                              <div className="flex justify-between mb-2"><div><div className="flex items-center gap-2 mb-1">{!d ? <Icon size={16} className="text-slate-500" /> : null}<span className="font-bold text-slate-800">{d?.name || i.status}</span></div><p className="text-xs text-slate-400">{i.date}</p></div><div className="flex items-center gap-2">{d && <StatusBadge status={i.status} />}<button onClick={() => onDeleteInteraction(i.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500"><Trash2 size={14} /></button></div></div>
                              {i.amount > 0 && d && <div className="mt-2 text-sm bg-emerald-50 p-2 rounded border border-emerald-100 flex gap-4"><div><span className="text-emerald-700 text-xs uppercase block">Invested</span><span className="font-bold">{formatCurrency(i.amount, d.currency)}</span></div></div>}
                              {i.notes && <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-2 italic">"{i.notes}"</p>}
                            </div>
                        </div>
                    )})}
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}

function DealsList({ deals, setActiveView, setSelectedDeal, onAddDeal, onEditDeal, onDeleteDeal }) {
  const [sort, setSort] = useState('status');
  const sorted = useMemo(() => [...deals].sort((a, b) => {
     if(sort === 'value') return (parseFloat(b.targetAmount)||0) - (parseFloat(a.targetAmount)||0);
     if(sort === 'newest') return (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0);
     return ({ 'Active': 0, 'On Hold': 1, 'Closed': 2 }[a.status]??9) - ({ 'Active': 0, 'On Hold': 1, 'Closed': 2 }[b.status]??9);
  }), [deals, sort]);

  return (
    <div className="h-full flex flex-col pb-6">
      <div className="flex justify-between items-center mb-6"><h1 className={`text-2xl font-bold ${BRAND.textPrimary}`}>Deal Pipeline</h1><div className="flex gap-3"><select className="appearance-none pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none" value={sort} onChange={e => setSort(e.target.value)}><option value="status">Status</option><option value="value">Value</option><option value="newest">Newest</option></select><button onClick={onAddDeal} className={`${BRAND.primary} ${BRAND.primaryHover} text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg font-bold`}><Plus size={18} /> New Deal</button></div></div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 overflow-hidden flex flex-col">
        <div className="overflow-y-auto flex-1 p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
           {sorted.map(d => (
             <div key={d.id} onClick={() => { setSelectedDeal(d); setActiveView('deal-detail'); }} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-400 hover:shadow-lg cursor-pointer transition-all flex flex-col justify-between h-48 group">
               <div><div className="flex justify-between items-start mb-2"><span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${d.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : d.status === 'On Hold' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>{d.status}</span><div className="flex gap-1 opacity-0 group-hover:opacity-100"><button onClick={(e) => { e.stopPropagation(); onEditDeal(d); }} className="p-1 hover:text-emerald-600"><Pencil size={16} /></button><button onClick={(e) => { e.stopPropagation(); onDeleteDeal(d.id); }} className="p-1 hover:text-red-600"><Trash2 size={16} /></button></div></div><h3 className="font-bold text-lg text-slate-800 mb-1 line-clamp-1 group-hover:text-emerald-700">{d.name}</h3><p className="text-sm text-slate-500 mb-4 line-clamp-2">{d.description || 'No description.'}</p></div>
               <div className="pt-4 border-t border-slate-100 flex justify-between text-sm"><span className="text-slate-500 font-medium">Target</span><span className="font-bold text-slate-800">{d.targetAmount ? formatCurrency(d.targetAmount, d.currency) : '-'}</span></div>
             </div>
           ))}
        </div>
      </div>
    </div>
  );
}

function DealDetail({ deal, contacts, interactions, documents, onBack, onAddInteraction, onDeleteInteraction, onEditDeal, onEditInteraction, setSelectedContact, setActiveView, onAddDocument, onDeleteDocument }) {
  const [tab, setTab] = useState('pipeline');
  const currency = deal.currency || 'USD';
  const dealInts = interactions.filter(i => i.dealId === deal.id);
  const committed = dealInts.filter(i => ['Invested', 'Committed'].includes(i.status)).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const progress = deal.targetAmount ? Math.min((committed / deal.targetAmount) * 100, 100) : 0;
  
  const investors = dealInts.filter(i => i.status === 'Invested');
  const totals = investors.reduce((acc, inv) => {
    const c = calculateCommissions(inv.amount, inv.commRate, inv.payawayRate, inv.nomadShare);
    return { raised: acc.raised + (parseFloat(inv.amount)||0), gross: acc.gross + c.grossCommission, pay: acc.pay + c.payawayAmount, nom: acc.nom + c.nomadAmount, net: acc.net + c.finalNet };
  }, { raised: 0, gross: 0, pay: 0, nom: 0, net: 0 });

  const PIPELINE_CONFIG = [
    { key: 'Approached', color: 'border-blue-500', badge: 'bg-blue-100 text-blue-800' },
    { key: 'Pitched', color: 'border-indigo-500', badge: 'bg-indigo-100 text-indigo-800' },
    { key: 'Interested', color: 'border-yellow-500', badge: 'bg-yellow-100 text-yellow-800' },
    { key: 'Invested', title: 'Invested / Committed', match: ['Invested', 'Committed'], color: 'border-emerald-500', badge: 'bg-emerald-100 text-emerald-800' },
    { key: 'Passed', color: 'border-slate-300', badge: 'bg-slate-100 text-slate-600' }
  ];

  return (
    <div className="h-full flex flex-col space-y-6 pb-6 animate-in slide-in-from-right-8 duration-300">
      <div className="flex flex-col md:flex-row md:items-center gap-4 flex-shrink-0">
        <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full text-slate-500"><ArrowRightLeft size={20} /></button>
        <div className="flex-1"><h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">{deal.name}<span className={`text-xs px-2 py-1 rounded-full border ${deal.status === 'Active' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600'}`}>{deal.status}</span></h1></div>
        <div className="flex bg-slate-200 rounded-lg p-1 gap-1">{[{ id: 'pipeline', l: 'Pipeline', i: Columns }, { id: 'financials', l: 'Financials', i: Table }, { id: 'documents', l: 'Docs', i: Files }].map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${tab === t.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}><t.i size={14} className="inline mr-2" />{t.l}</button>)}</div>
        <div className="flex gap-2"><button onClick={() => onEditDeal(deal)} className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium hover:bg-slate-50"><Pencil size={16} /> Edit</button><button onClick={() => tab === 'documents' ? onAddDocument(deal.id) : onAddInteraction(null, deal.id)} className={`${BRAND.primary} ${BRAND.primaryHover} text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2 font-bold`}><Plus size={16} /> Add {tab === 'documents' ? 'Link' : 'Investor'}</button></div>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex-shrink-0">
        <div className="flex justify-between items-end mb-2"><div><p className="text-sm text-slate-500 mb-1">Capital Raised</p><p className="text-3xl font-bold text-slate-900">{formatCurrency(committed, currency)} <span className="text-lg text-slate-400 font-normal">/ {formatCurrency(parseInt(deal.targetAmount || 0), currency)}</span></p></div><p className="text-emerald-600 font-bold">{Math.round(progress)}%</p></div>
        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden"><div className="bg-emerald-500 h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${progress}%` }}></div></div>
      </div>
      <SmartMatch deal={deal} contacts={contacts} onMatchClick={(c) => onAddInteraction(c.id, deal.id, 'Approached')} />
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {tab === 'pipeline' && (
           <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 flex-1 overflow-x-auto pb-4 min-h-0">
             {PIPELINE_CONFIG.map(col => (
               <PipelineColumn key={col.key} title={col.title || col.key} items={dealInts.filter(i => (col.match || [col.key]).includes(i.status))} contacts={contacts} color={col.color} currency={currency} onAdd={() => onAddInteraction(null, deal.id, col.key)} onDelete={onDeleteInteraction} onEdit={onEditInteraction} onSelectContact={(cid) => { setSelectedContact(contacts.find(c => c.id === cid)); setActiveView('contact-detail'); }} />
             ))}
           </div>
        )}
        {tab === 'financials' && (
           <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col flex-1 overflow-hidden animate-in fade-in min-h-0">
             <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center"><h3 className="font-bold text-slate-700 text-sm uppercase">Placement Sheet</h3><button onClick={() => alert("Downloading CSV")} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-2 font-medium"><Download size={14} /> Export</button></div>
             <div className="overflow-auto flex-1"><table className="w-full text-sm text-left whitespace-nowrap"><thead className="text-xs text-slate-500 uppercase bg-slate-50 sticky top-0 border-b border-slate-200 z-10"><tr><th className="px-4 py-3">Investor</th><th className="px-4 py-3 text-right">Raised</th><th className="px-4 py-3 text-right">Comm Rate</th><th className="px-4 py-3 text-right">Gross Comm</th><th className="px-4 py-3 text-right">Payaway</th><th className="px-4 py-3 text-right">Nomad Split</th><th className="px-4 py-3 text-right font-bold text-slate-700">MCL Net</th><th className="px-4 py-3 text-center">Actions</th></tr></thead><tbody className="divide-y divide-slate-100">
                {investors.map(inv => { const c = calculateCommissions(inv.amount, inv.commRate, inv.payawayRate, inv.nomadShare); return <tr key={inv.id} className="hover:bg-slate-50 group"><td className="px-4 py-3 font-medium cursor-pointer hover:text-emerald-700" onClick={() => { setSelectedContact(contacts.find(x => x.id === inv.contactId)); setActiveView('contact-detail'); }}>{contacts.find(x => x.id === inv.contactId)?.name}</td><td className="px-4 py-3 text-right font-mono">{formatCurrency(inv.amount, currency)}</td><td className="px-4 py-3 text-right text-slate-500">{formatPercent(inv.commRate)}</td><td className="px-4 py-3 text-right text-slate-500">{formatCurrency(c.grossCommission, currency)}</td><td className="px-4 py-3 text-right text-red-400">-{formatCurrency(c.payawayAmount, currency)}</td><td className="px-4 py-3 text-right text-red-400">-{formatCurrency(c.nomadAmount, currency)}</td><td className="px-4 py-3 text-right font-bold text-emerald-700 bg-emerald-50/50">{formatCurrency(c.finalNet, currency)}</td><td className="px-4 py-3 text-center"><div className="flex justify-center gap-2 opacity-0 group-hover:opacity-100"><button onClick={() => onEditInteraction(inv)}><Pencil size={14} /></button><button onClick={() => onDeleteInteraction(inv.id)}><Trash2 size={14} /></button></div></td></tr>; })}
             </tbody><tfoot className="bg-slate-100 border-t border-slate-200 font-bold sticky bottom-0 z-10"><tr><td className="px-4 py-3">TOTALS</td><td className="px-4 py-3 text-right">{formatCurrency(totals.raised, currency)}</td><td colSpan="2" className="px-4 py-3 text-right">{formatCurrency(totals.gross, currency)}</td><td className="px-4 py-3 text-right text-red-600">-{formatCurrency(totals.pay, currency)}</td><td className="px-4 py-3 text-right text-red-600">-{formatCurrency(totals.nom, currency)}</td><td className="px-4 py-3 text-right text-emerald-700 bg-emerald-100">{formatCurrency(totals.net, currency)}</td><td></td></tr></tfoot></table></div>
           </div>
        )}
        {tab === 'documents' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 overflow-hidden flex flex-col animate-in fade-in min-h-0">
             <div className="p-4 border-b border-slate-200 bg-slate-50"><h3 className="font-bold text-slate-700 text-sm uppercase">Deal Room</h3></div>
             <div className="p-6 overflow-y-auto flex-1">{documents.filter(d => d.dealId === deal.id).length === 0 ? <div className="text-center text-slate-400 mt-10"><Files size={32} className="mx-auto mb-2" /><p>No documents.</p></div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{documents.filter(d => d.dealId === deal.id).map(doc => (
                <a key={doc.id} href={doc.url} target="_blank" rel="noreferrer" className="flex items-start p-4 border border-slate-200 rounded-xl hover:border-emerald-400 hover:shadow-md transition-all group bg-white relative">
                   <div className="p-3 bg-slate-50 rounded-lg mr-4">{doc.type === 'Pitch Deck' ? <Presentation className="text-orange-500" /> : doc.type === 'Financial Model' ? <FileSpreadsheet className="text-emerald-500" /> : <File className="text-slate-400" />}</div>
                   <div className="flex-1 min-w-0"><div className="flex justify-between"><span className="text-xs font-bold text-slate-500 uppercase mb-1">{doc.type}</span><button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteDocument(doc.id); }} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button></div><h4 className="font-bold text-slate-800 truncate mb-1">{doc.title}</h4><div className="flex items-center text-xs text-slate-400 gap-1"><ExternalLink size={10} /><span className="truncate">{doc.url}</span></div></div>
                </a>
             ))}</div>}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- FORMS ---
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-visible animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10"><h3 className="font-bold text-lg text-slate-800">{title}</h3><button onClick={onClose}><X size={20} className="text-slate-500" /></button></div><div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function AddInteractionForm({ onClose, contacts, deals, interactions, initialContactId, initialDealId, initialStatus, initialData }) {
  const [f, setF] = useState({ contactId: initialContactId||'', dealId: initialDealId||'', status: initialStatus||'Approached', amount: '', notes: '', date: new Date().toISOString().split('T')[0], commRate: '0.06', payawayRate: '0.00', nomadShare: '0.20', exchangeRate: '1.0' });
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(initialDealId ? 'deal' : 'general');
  const dObj = deals.find(d => d.id === f.dealId);
  const cObj = contacts.find(c => c.id === f.contactId);

  useEffect(() => { if(initialData) { setF({...initialData, exchangeRate: initialData.exchangeRate?.toString()||'1.0'}); setMode(initialData.dealId ? 'deal' : 'general'); }}, [initialData]);
  useEffect(() => { if(!initialData && f.dealId && f.status === 'Invested') setF(p => ({...p, exchangeRate: (EXCHANGE_RATES[dObj?.currency||'USD']||1).toString()})); }, [f.dealId, f.status, dObj, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    if (!f.contactId) return alert("Please select a client");
    if (mode === 'deal' && !f.dealId) return alert("Please select a deal");
    
    setLoading(true);
    const data = { ...f, dealId: mode === 'deal' ? f.dealId : null };
    if (mode === 'deal' && f.status === 'Invested') { data.commRate = f.commRate; data.payawayRate = f.payawayRate; data.nomadShare = f.nomadShare; data.exchangeRate = parseFloat(f.exchangeRate)||1; }
    try {
      if (initialData) {
        await updateDoc(doc(db, ...DB_PATH_SEGMENTS, 'interactions', initialData.id), data);
        await logAudit('UPDATE', `Updated interaction for ${cObj ? cObj.name : 'Unknown'}`, { type: 'contact', id: f.contactId, name: cObj?.name });
      } else {
        await addDoc(collection(db, ...DB_PATH_SEGMENTS, 'interactions'), { ...data, createdAt: serverTimestamp() });
        await logAudit('CREATE', `Logged ${f.status} for ${cObj ? cObj.name : 'Unknown'}`, { type: 'contact', id: f.contactId, name: cObj?.name });
      }
      onClose();
    } catch { alert("Error logging."); } finally { setLoading(false); }
  };
  const prev = calculateCommissions(f.amount, f.commRate, f.payawayRate, f.nomadShare);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!initialData && <div className="flex bg-slate-100 p-1 rounded-lg mb-4"><button type="button" onClick={() => setMode('deal')} className={`flex-1 py-2 text-sm font-bold rounded-md ${mode === 'deal' ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500'}`}>Deal Update</button><button type="button" onClick={() => { setMode('general'); setF({...f, status: 'Call'}); }} className={`flex-1 py-2 text-sm font-bold rounded-md ${mode === 'general' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500'}`}>General Activity</button></div>}
      {(!initialContactId && !initialData) && (
        <SearchableSelect 
          label="Client *"
          placeholder="Search for client..."
          options={contacts.map(c => ({ value: c.id, label: c.name }))}
          value={f.contactId}
          onChange={(val) => setF({...f, contactId: val})}
        />
      )}
      {mode === 'deal' && (!initialDealId && !initialData) && (
        <SearchableSelect 
          label="Deal *"
          placeholder="Search for deal..."
          options={deals.map(d => ({ value: d.id, label: d.name }))}
          value={f.dealId}
          onChange={(val) => setF({...f, dealId: val})}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label><select className="w-full p-2 border border-slate-300 rounded-lg outline-none bg-white" value={f.status} onChange={e => setF({...f, status: e.target.value})}>{mode === 'deal' ? ['Approached', 'Pitched', 'Interested', 'Invested', 'Passed'].map(o => <option key={o} value={o}>{o}</option>) : ['Call', 'Meeting', 'Lunch', 'Email', 'Other'].map(o => <option key={o} value={o}>{o}</option>)}</select></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1">Date</label><input className="w-full p-2 border border-slate-300 rounded-lg outline-none" type="date" value={f.date} onChange={e => setF({...f, date: e.target.value})} /></div>
      </div>
      {mode === 'deal' && ['Invested','Interested'].includes(f.status) && <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount ({dObj?.currency})</label><input className="w-full p-2 border border-slate-300 rounded-lg outline-none font-mono" type="number" value={f.amount} onChange={e => setF({...f, amount: e.target.value})} placeholder="0.00" /></div>}
      {mode === 'deal' && f.status === 'Invested' && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-blue-50/50 p-2 rounded border border-blue-100 flex items-center gap-2"><span className="text-xs font-semibold text-blue-700">Rate (to GBP):</span><input className="flex-1 p-1 bg-white border border-blue-200 rounded text-sm font-mono" type="number" step="0.0001" value={f.exchangeRate} onChange={e => setF({...f, exchangeRate: e.target.value})} /></div>
            {[ {l: 'Comm %', k: 'commRate'}, {l: 'Payaway %', k: 'payawayRate'} ].map(i => <div key={i.k}><label className="text-xs font-semibold text-slate-500">{i.l}</label><input className="w-full p-2 border rounded-lg text-sm" type="number" step="0.01" value={f[i.k]} onChange={e => setF({...f, [i.k]: e.target.value})} /></div>)}
            <div className="col-span-2"><label className="text-xs font-semibold text-slate-500">Nomad Share</label><input className="w-full p-2 border rounded-lg text-sm" type="number" step="0.01" value={f.nomadShare} onChange={e => setF({...f, nomadShare: e.target.value})} /></div>
          </div>
          <div className="pt-3 border-t border-slate-200 grid grid-cols-2 gap-2 text-xs">
             <div><span className="text-slate-500">Gross:</span><div className="font-mono font-bold text-slate-700">{formatCurrency(prev.grossCommission, dObj?.currency)}</div></div>
             <div><span className="text-emerald-700 font-bold">Net Revenue:</span><div className="font-mono font-bold text-emerald-600 text-sm">{formatCurrency(prev.finalNet, dObj?.currency)}</div></div>
          </div>
        </div>
      )}
      <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label><textarea className="w-full p-2 border border-slate-300 rounded-lg outline-none" rows={3} value={f.notes} onChange={e => setF({...f, notes: e.target.value})} /></div>
      <button type="submit" disabled={loading} className={`w-full ${BRAND.primary} ${BRAND.primaryHover} text-white py-2.5 rounded-lg font-bold transition-colors disabled:opacity-50`}>{loading ? 'Saving...' : 'Save Record'}</button>
    </form>
  );
}

// --- SUB-FORMS ---
function ContactForm({ onClose, initialData }) {
  const [f, setF] = useState(initialData || { name: '', company: '', email: '', phone: '', tags: '', notes: '' });
  const sub = async (e) => { 
    e.preventDefault(); 
    try { 
      if (initialData) {
        await updateDoc(doc(db, ...DB_PATH_SEGMENTS, 'contacts', initialData.id), f);
        await logAudit('UPDATE', `Updated client: ${f.name}`, { type: 'contact', id: initialData.id, name: f.name });
      } else {
        const ref = await addDoc(collection(db, ...DB_PATH_SEGMENTS, 'contacts'), { ...f, createdAt: serverTimestamp() });
        await logAudit('CREATE', `Created client: ${f.name}`, { type: 'contact', id: ref.id, name: f.name });
      }
      onClose(); 
    } catch(e){console.error(e);} 
  };
  return <form onSubmit={sub} className="space-y-4"><div><label className="text-sm font-bold text-slate-700">Name</label><input className="w-full p-2 border rounded" value={f.name} onChange={e => setF({...f, name: e.target.value})} required /></div><input className="w-full p-2 border rounded" placeholder="Company" value={f.company} onChange={e => setF({...f, company: e.target.value})} /><div className="grid grid-cols-2 gap-2"><input className="w-full p-2 border rounded" placeholder="Email" value={f.email} onChange={e => setF({...f, email: e.target.value})} /><input className="w-full p-2 border rounded" placeholder="Phone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} /></div><TagInput value={f.tags} onChange={val => setF({...f, tags: val})} placeholder="Type tag and press Enter" /><textarea className="w-full p-2 border rounded" placeholder="Notes" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} /><button className={`${BRAND.primary} text-white w-full py-2 rounded font-bold`}>Save</button></form>;
}

function DealForm({ onClose, initialData }) {
  const [f, setF] = useState(initialData || { name: '', targetAmount: '', currency: 'USD', status: 'Active', description: '', tags: '' });
  const sub = async (e) => { 
    e.preventDefault(); 
    try { 
      if (initialData) {
        await updateDoc(doc(db, ...DB_PATH_SEGMENTS, 'deals', initialData.id), f);
        await logAudit('UPDATE', `Updated deal: ${f.name}`, { type: 'deal', id: initialData.id, name: f.name });
      } else {
        const ref = await addDoc(collection(db, ...DB_PATH_SEGMENTS, 'deals'), { ...f, createdAt: serverTimestamp() });
        await logAudit('CREATE', `Created deal: ${f.name}`, { type: 'deal', id: ref.id, name: f.name });
      }
      onClose(); 
    } catch(e){console.error(e);} 
  };
  return <form onSubmit={sub} className="space-y-4"><div><label className="text-sm font-bold text-slate-700">Name</label><input className="w-full p-2 border rounded" value={f.name} onChange={e => setF({...f, name: e.target.value})} required /></div><div className="grid grid-cols-2 gap-2"><input type="number" className="w-full p-2 border rounded" placeholder="Target" value={f.targetAmount} onChange={e => setF({...f, targetAmount: e.target.value})} /><select className="w-full p-2 border rounded" value={f.currency} onChange={e => setF({...f, currency: e.target.value})}>{Object.keys(CURRENCIES).map(c => <option key={c} value={c}>{c}</option>)}</select></div><select className="w-full p-2 border rounded" value={f.status} onChange={e => setF({...f, status: e.target.value})}>{'Active On Hold Closed'.split(' ').map(s=><option key={s} value={s}>{s}</option>)}</select><TagInput value={f.tags} onChange={val => setF({...f, tags: val})} placeholder="Type tag and press Enter" /><textarea className="w-full p-2 border rounded" placeholder="Description" value={f.description} onChange={e => setF({...f, description: e.target.value})} /><button className={`${BRAND.primary} text-white w-full py-2 rounded font-bold`}>Save</button></form>;
}

function AddDocumentForm({ onClose, dealId }) {
  const [f, setF] = useState({ title: '', url: '', type: 'General' });
  const sub = async (e) => { 
    e.preventDefault(); 
    try { 
      await addDoc(collection(db, ...DB_PATH_SEGMENTS, 'documents'), { ...f, dealId, createdAt: serverTimestamp() }); 
      await logAudit('CREATE', `Linked document: ${f.title}`);
      onClose(); 
    } catch(e){console.error(e);} 
  };
  return <form onSubmit={sub} className="space-y-4"><input className="w-full p-2 border rounded" placeholder="Title" value={f.title} onChange={e => setF({...f, title: e.target.value})} required /><input className="w-full p-2 border rounded" placeholder="URL" value={f.url} onChange={e => setF({...f, url: e.target.value})} required /><select className="w-full p-2 border rounded" value={f.type} onChange={e => setF({...f, type: e.target.value})}>{['General','Pitch Deck','Financial Model','Legal','One Pager'].map(t=><option key={t} value={t}>{t}</option>)}</select><button className={`${BRAND.primary} text-white w-full py-2 rounded font-bold`}>Link</button></form>;
}

function QuickAddDealForm({ onClose, deals, contactId, contactName }) {
  const [did, setDid] = useState('');
  const sub = async (e) => { 
    e.preventDefault(); 
    const dealName = deals.find(d => d.id === did)?.name || 'Unknown Deal';
    try { 
      await addDoc(collection(db, ...DB_PATH_SEGMENTS, 'interactions'), { contactId, dealId: did, status: 'Approached', date: new Date().toISOString().split('T')[0], createdAt: serverTimestamp() }); 
      await logAudit('CREATE', `Quick added ${contactName} to deal`, { type: 'deal', id: did, name: dealName });
      onClose(); 
    } catch(e){console.error(e);} 
  };
  return (
    <form onSubmit={sub} className="space-y-4">
      <SearchableSelect 
          placeholder="Search for deal..."
          options={deals.filter(d => d.status === 'Active').map(d => ({ value: d.id, label: d.name }))}
          value={did}
          onChange={(val) => setDid(val)}
      />
      <button className={`${BRAND.primary} text-white w-full py-2 rounded font-bold`}>Add</button>
    </form>
  );
}

// --- MAIN APP ---
export default function MercuryCarbonCRM() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('dashboard');
  const [selContact, setSelContact] = useState(null);
  const [selDeal, setSelDeal] = useState(null);
  const [modals, setModals] = useState({ contact: false, deal: false, interact: false, doc: false, quick: false });
  const [edits, setEdits] = useState({ contact: null, deal: null, interact: null });
  const [defaults, setDefaults] = useState({});
  const [data, setData] = useState({ contacts: [], deals: [], interactions: [], documents: [], audit_logs: [] });

  useEffect(() => {
    if(window.__initial_auth_token) signInWithCustomToken(auth, window.__initial_auth_token).catch(() => {});
    return onAuthStateChanged(auth, (u) => setUser(u?.email?.endsWith('@mercurycarbon.com') ? u : null));
  }, []);

  useEffect(() => {
    if (!user) return;
    const q = (c) => query(collection(db, ...DB_PATH_SEGMENTS, c), orderBy('createdAt', 'desc'));
    const unsubs = ['contacts', 'deals', 'interactions', 'documents', 'audit_logs'].map(k => onSnapshot(q(k), s => setData(p => ({ ...p, [k]: s.docs.map(d => ({ id: d.id, ...d.data() })) }))));
    return () => unsubs.forEach(u => u());
  }, [user]);

  // Sync selected contact/deal when data updates (Instant Refresh Fix)
  useEffect(() => {
    if (selContact) {
      const updated = data.contacts.find(c => c.id === selContact.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selContact)) {
        setSelContact(updated);
      }
    }
  }, [data.contacts, selContact]);

  useEffect(() => {
    if (selDeal) {
      const updated = data.deals.find(d => d.id === selDeal.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selDeal)) {
        setSelDeal(updated);
      }
    }
  }, [data.deals, selDeal]);

  const toggle = (k, v = true) => setModals(p => ({ ...p, [k]: v }));
  
  const handleDel = async (c, id, confirmMsg) => { 
    if(window.confirm(confirmMsg)) {
      try { 
        // Resolve name before deletion for audit log
        let name = id;
        let type = null;
        if (c === 'contacts') {
           const item = data.contacts.find(i => i.id === id);
           name = item ? item.name : id;
           type = 'contact';
        } else if (c === 'deals') {
           const item = data.deals.find(i => i.id === id);
           name = item ? item.name : id;
           type = 'deal';
        }

        await deleteDoc(doc(db, ...DB_PATH_SEGMENTS, c, id)); 
        await logAudit('DELETE', `Deleted ${type || c.slice(0, -1)}: ${name}`, { type, id, name });
        
        if(c==='contacts'&&selContact?.id===id) setSelContact(null); 
        if(c==='deals'&&selDeal?.id===id) setSelDeal(null); 
      } catch(e) { console.error(e); } 
    }
  };

  const handleNavigate = (type, id) => {
    if (type === 'contact') {
      const c = data.contacts.find(x => x.id === id);
      if (c) { setSelContact(c); setView('contact-detail'); }
    } else if (type === 'deal') {
      const d = data.deals.find(x => x.id === id);
      if (d) { setSelDeal(d); setView('deal-detail'); }
    }
  };

  if (!user) return <LoginScreen />;

  return (
    <div className="flex h-screen bg-slate-100 font-sans text-slate-900 overflow-hidden">
      <Sidebar activeView={view} setActiveView={setView} user={user} />
      <main className="flex-1 overflow-hidden flex flex-col relative">
        <div className="flex-1 overflow-y-auto p-4 lg:p-8 relative custom-scrollbar">
          {view === 'dashboard' && <Dashboard {...data} setActiveView={setView} setSelectedDeal={setSelDeal} />}
          {view === 'calendar' && <CalendarView {...data} onEventClick={(i) => { setEdits({...edits, interact: i}); toggle('interact'); }} onDeleteEvent={(id) => handleDel('interactions', id, 'Delete?')} setSelectedContact={setSelContact} setSelectedDeal={setSelDeal} setActiveView={setView} />}
          {view === 'history' && <HistoricalStats deals={data.deals} interactions={data.interactions} setActiveView={setView} setSelectedDeal={setSelDeal} />}
          {view === 'calculator' && <RevenueCalculator />}
          {view === 'contacts' && <ContactsList {...data} setActiveView={setView} setSelectedContact={setSelContact} onAddContact={() => { setEdits({...edits, contact: null}); toggle('contact'); }} onEditContact={(c) => { setEdits({...edits, contact: c}); toggle('contact'); }} onDeleteContact={(id) => handleDel('contacts', id, 'Delete client?')} onQuickAddToDeal={(c) => { setDefaults({ quickContact: c }); toggle('quick'); }} />}
          {view === 'contact-detail' && selContact && <ContactDetail contact={selContact} {...data} onBack={() => setView('contacts')} onAddInteraction={(cid) => { setDefaults({ cid, did: null, stat: null }); setEdits({...edits, interact: null}); toggle('interact'); }} onDeleteInteraction={(id) => handleDel('interactions', id, 'Delete log?')} onEditContact={(c) => { setEdits({...edits, contact: c}); toggle('contact'); }} setSelectedDeal={setSelDeal} setActiveView={setView} />}
          {view === 'deals' && <DealsList deals={data.deals} setActiveView={setView} setSelectedDeal={setSelDeal} onAddDeal={() => { setEdits({...edits, deal: null}); toggle('deal'); }} onEditDeal={(d) => { setEdits({...edits, deal: d}); toggle('deal'); }} onDeleteDeal={(id) => handleDel('deals', id, 'Delete deal?')} />}
          {view === 'deal-detail' && selDeal && <DealDetail deal={selDeal} {...data} onBack={() => setView('deals')} onAddInteraction={(cid, did, stat) => { setDefaults({ cid, did, stat }); setEdits({...edits, interact: null}); toggle('interact'); }} onDeleteInteraction={(id) => handleDel('interactions', id, 'Delete?')} onEditInteraction={(i) => { setEdits({...edits, interact: i}); toggle('interact'); }} onEditDeal={(d) => { setEdits({...edits, deal: d}); toggle('deal'); }} setSelectedContact={setSelContact} setActiveView={setView} onAddDocument={(did) => { setDefaults({ did }); toggle('doc'); }} onDeleteDocument={(id) => handleDel('documents', id, 'Delete doc?')} />}
          {view === 'audit' && <AuditLogView logs={data.audit_logs} contacts={data.contacts} deals={data.deals} onNavigate={handleNavigate} />}
        </div>
      </main>
      
      {modals.contact && <Modal title={edits.contact ? "Edit Client" : "Add Client"} onClose={() => toggle('contact', false)}><ContactForm onClose={() => toggle('contact', false)} initialData={edits.contact} /></Modal>}
      {modals.deal && <Modal title={edits.deal ? "Edit Deal" : "New Deal"} onClose={() => toggle('deal', false)}><DealForm onClose={() => toggle('deal', false)} initialData={edits.deal} /></Modal>}
      {modals.interact && <Modal title={edits.interact ? "Edit Activity" : "Log Activity"} onClose={() => toggle('interact', false)}><AddInteractionForm onClose={() => toggle('interact', false)} contacts={data.contacts} deals={data.deals} interactions={data.interactions} initialContactId={defaults.cid} initialDealId={defaults.did} initialStatus={defaults.stat} initialData={edits.interact} /></Modal>}
      {modals.doc && <Modal title="Link Document" onClose={() => toggle('doc', false)}><AddDocumentForm onClose={() => toggle('doc', false)} dealId={defaults.did} /></Modal>}
      {modals.quick && defaults.quickContact && <Modal title={`Add ${defaults.quickContact.name}`} onClose={() => toggle('quick', false)}><QuickAddDealForm onClose={() => toggle('quick', false)} deals={data.deals} contactId={defaults.quickContact.id} contactName={defaults.quickContact.name} /></Modal>}
    </div>
  );
}